import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCalendar,
  IconCheck,
  IconClipboard,
  IconDots,
  IconPrinter,
  IconUser,
  IconUserCheck,
  IconX,
} from "@tabler/icons-react";
import type { EventPreviewData } from "../../../../types/calendar";
import type { AppointmentForm } from "../../../../types/appointmentForms";
import type { GetTransactionsResponse } from "../../../../types/transactions";
import type { AppointmentTag } from "../../../../types/appointmentTags";
import type { Client, PotentialDuplicateGroup } from "../../../../types/client";
import { useClient } from "../../../../context/ClientContext";
import usePermissions from "../../../../hooks/usePermissions";
import { useAppointmentForms } from "../../../../hooks/useAppointmentForms";
import { isClientPotentialDuplicate } from "../../../../utils/duplicateGrouping";
import { DEFAULT_TIMEZONE } from "../../../../utils/timeUtils";
import AppointmentStatusBadgeMenu from "../../../common/AppointmentStatusBadgeMenu";
import RefundStatusBadge from "../../../common/RefundStatusBadge";
import DuplicateClientWarningIcon from "../../../common/DuplicateClientWarningIcon";
import {
  appointmentStatusDisplay,
  canReschedule,
  statusPickBlockedReason,
} from "../booking/guards";
import CalendarDrawer from "../drawer/CalendarDrawer";
import SummaryRow from "../drawer/SummaryRow";
import { timeRangeLabel, type EventFlag } from "../components/Views/EventBlock";
import {
  appointmentEnded,
  arrivalArea,
  CLOSED_STATUSES,
  formatDurationLong,
  scheduledMessagesCount,
} from "./appointmentModel";
import NeedToKnow from "./NeedToKnow";
import AppointmentTagsRow from "./AppointmentTagsRow";
import { withoutTagsFlag, withPinnedNotes } from "../flags/calendarFlags";
import type { BadgeSettings } from "../../../../utils/badgeSettings";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import { FormsScreen, MessagesScreen, NotesScreen } from "./AppointmentScreens";
import { usePreviewClientNotes } from "./usePreviewClientNotes";
import styles from "./AppointmentDrawer.module.css";

export type AppointmentNested = "forms" | "notes" | "messages";

export interface AppointmentDrawerProps {
  previewData: EventPreviewData | null;
  salonUuid?: string;
  timezone?: string;
  onClose: () => void;
  /** The slot's open nested screen (drawer slot `nested`). */
  nested: string | null;
  onOpenNested: (nested: AppointmentNested) => void;
  onCloseNested: () => void;
  /** Checkout / View details: the existing TransactionDetailModal. */
  onCheckout: () => void;
  onStatusUpdate?: (status: string) => void | Promise<void>;
  onReschedule?: (txn: GetTransactionsResponse) => void;
  onRebook?: (txn: GetTransactionsResponse) => void;
  onOpenForm?: (txn: GetTransactionsResponse, formId: string) => void;
  onAddForm?: (txn: GetTransactionsResponse) => void;
  /** The drawer removed / changed forms: sync the calendar store. */
  onFormsChange?: (forms: AppointmentForm[]) => void;
  formsAlreadySent?: boolean;
  onFormsSent?: (transactionUuid: string) => void;
  onViewClient?: (client: Client) => void;
  /** "Need to know" rows (calendarFlags). */
  flags: EventFlag[];
  /** Badge toggles: the drawer's pinned-notes row respects them. */
  badgeSettings?: BadgeSettings;
  /** The Tags row saved: sync the calendar store (no row when absent). */
  onTagsChange?: (tags: AppointmentTag[]) => void;
  /** Print this appointment's receipt (header printer icon; hidden when absent). */
  onPrintReceipt?: (txn: GetTransactionsResponse) => void;
}

const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean)));

/**
 * The appointment drawer (design handoff §4), in the calendar drawer slot as
 * kind "appointment" (it replaces the event preview popover). Status pill,
 * client, facts, Tags, the request / arrival area, Need to know, Forms / Notes /
 * Scheduled messages summary rows with their nested screens, and a sticky
 * footer: Checkout (the unchanged Appointment Details modal), Edit /
 * Reschedule, Prebook and More (no-show, cancel). Existing data and actions
 * only.
 */
export default function AppointmentDrawer(props: AppointmentDrawerProps) {
  const { previewData, onClose, nested, onCloseNested } = props;
  const transaction = previewData?.transaction;
  const opened = !!previewData && !!transaction;

  // Escape: back out of a nested screen first, then close.
  useEffect(() => {
    if (!opened) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // A modal on top (Appointment Details, confirms) owns Escape.
      if (document.querySelector(".mantine-Modal-content")) return;
      if (nested) onCloseNested();
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [opened, nested, onClose, onCloseNested]);

  return (
    <CalendarDrawer
      opened={opened}
      onClose={onClose}
      withHeader={false}
      ariaLabel="Appointment"
    >
      {opened && (
        <DrawerBody
          // Fresh local state (confirms, drafts) per appointment.
          key={transaction.transaction.id}
          {...props}
          transaction={transaction}
        />
      )}
    </CalendarDrawer>
  );
}

function DrawerBody({
  previewData,
  transaction,
  salonUuid,
  timezone = DEFAULT_TIMEZONE,
  onClose,
  nested,
  onOpenNested,
  onCloseNested,
  onCheckout,
  onStatusUpdate,
  onReschedule,
  onRebook,
  onOpenForm,
  onAddForm,
  onFormsChange,
  formsAlreadySent,
  onFormsSent,
  onViewClient,
  flags,
  badgeSettings,
  onTagsChange,
  onPrintReceipt,
}: AppointmentDrawerProps & { transaction: GetTransactionsResponse }) {
  const [updating, setUpdating] = useState(false);
  const event = previewData!.event;
  const status = transaction.transaction.appointment_status;
  const txnId = transaction.transaction.id;

  const setStatus = async (next: string) => {
    if (!onStatusUpdate) return;
    try {
      setUpdating(true);
      await onStatusUpdate(next);
    } finally {
      setUpdating(false);
    }
  };

  // ── Client ──────────────────────────────────────────────────────────
  const { potentialDuplicates } = useClient() as {
    potentialDuplicates: PotentialDuplicateGroup[];
  };
  const notesApi = usePreviewClientNotes(transaction.client?.id);
  const client = transaction.client ?? notesApi.cachedClient;
  const clientName = client
    ? `${client.first_name} ${client.last_name}`.trim()
    : "Unknown Client";
  const isDuplicate = useMemo(
    () => isClientPotentialDuplicate(potentialDuplicates, client?.id),
    [potentialDuplicates, client?.id],
  );

  // ── Facts ───────────────────────────────────────────────────────────
  const services = transaction.line_items.booked_services || [];
  const products = transaction.line_items.products || [];
  const serviceLabel = [
    ...services.map((s) => s.name),
    ...products.map((p) => p.name),
  ].join(", ");
  const providers = uniq(services.map((s) => s.staff_name)).join(", ");
  // event.start/end are salon wall-clock (fake-local); format plainly.
  const minutes = (event.end.getTime() - event.start.getTime()) / 60000;
  const day = event.start.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const when = `${day} · ${timeRangeLabel(event.start, event.end)} · ${formatDurationLong(minutes)}`;
  const bookedUsingAnyAvailable =
    transaction.transaction.booked_using_any_available ?? false;

  // ── Forms / notes / messages ────────────────────────────────────────
  const canViewClientForms =
    usePermissions()?.formsAndWaivers?.canViewClientForms === true;
  const initialForms = useMemo(
    () => transaction.forms ?? [],
    [transaction.forms],
  );
  const formsApi = useAppointmentForms(txnId, {
    initialForms,
    salonUuid,
    clientUuid: transaction.client?.id,
    reseedOnInitialFormsChange: true,
    onFormsChange,
  });
  const messagesCount = scheduledMessagesCount(transaction);

  // ── Footer gating ───────────────────────────────────────────────────
  const ended = appointmentEnded(transaction, timezone);
  const closed = !!status && CLOSED_STATUSES.has(status);
  const showReschedule = !!onReschedule && canReschedule(transaction) && !ended;
  const showPrebook = !!onRebook && services.length > 0;
  const area = arrivalArea(transaction, timezone);

  // ── Nested screens ──────────────────────────────────────────────────
  if (nested === "forms" && canViewClientForms) {
    return (
      <FormsScreen
        transaction={transaction}
        formsApi={formsApi}
        status={status}
        onBack={onCloseNested}
        onOpenForm={onOpenForm}
        onAddForm={onAddForm}
        formsAlreadySent={formsAlreadySent}
        onFormsSent={onFormsSent}
      />
    );
  }
  if (nested === "notes") {
    return (
      <NotesScreen
        notesApi={notesApi}
        client={client ?? null}
        onBack={onCloseNested}
      />
    );
  }
  if (nested === "messages") {
    return (
      <MessagesScreen
        transactionUuid={txnId}
        timezone={timezone}
        hasPendingForms={formsApi.pendingCount > 0}
        status={status}
        onBack={onCloseNested}
      />
    );
  }

  return (
    <Box className={styles.body}>
      <Box className={styles.scroll}>
        {/* Header: status pill + print / close */}
        <Group justify="space-between" wrap="nowrap" className={styles.header}>
          <Group gap="xs" wrap="nowrap">
            {status && (
              <AppointmentStatusBadgeMenu
                currentStatus={status}
                display={appointmentStatusDisplay(
                  status,
                  transaction,
                  timezone,
                )}
                onChange={setStatus}
                getStatusBlockedReason={(next) =>
                  statusPickBlockedReason(next, transaction, timezone)
                }
                disabled={!onStatusUpdate}
                loading={updating}
                size="md"
                radius="xl"
              />
            )}
            <RefundStatusBadge
              refundStatus={transaction.transaction.refund_status}
              size="md"
            />
          </Group>
          <Group gap={4} wrap="nowrap">
            {onPrintReceipt && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size={32}
                radius="xl"
                onClick={() => onPrintReceipt(transaction)}
                aria-label="Print receipt"
              >
                <IconPrinter size={18} />
              </ActionIcon>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              size={32}
              radius="xl"
              onClick={onClose}
              aria-label="Close"
            >
              <IconX size={18} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Client */}
        <Group gap={6} wrap="nowrap" className={styles.clientRow}>
          {client ? (
            <UnstyledButton
              className={styles.clientName}
              onClick={() => onViewClient?.(client)}
            >
              {clientName}
              <DESIGN_ICONS.ChevronRight size={18} aria-hidden />
            </UnstyledButton>
          ) : (
            <Text className={styles.clientName}>{clientName}</Text>
          )}
          {isDuplicate && <DuplicateClientWarningIcon />}
        </Group>

        {/* Facts */}
        <Stack gap={6} className={styles.facts}>
          {serviceLabel && (
            <Fact icon={<IconClipboard size={15} />}>{serviceLabel}</Fact>
          )}
          <Fact icon={<IconCalendar size={15} />}>{when}</Fact>
          {providers && <Fact icon={<IconUser size={15} />}>{providers}</Fact>}
          {bookedUsingAnyAvailable && (
            <Text size="xs" c="dimmed" fs="italic">
              Booked using the &ldquo;Any Available&rdquo; option
            </Text>
          )}
        </Stack>

        {onTagsChange && (
          <AppointmentTagsRow
            transactionId={txnId}
            tags={transaction.tags ?? []}
            onChange={onTagsChange}
          />
        )}

        {/* Request / arrival (one at most) */}
        {area?.kind === "request" && (
          <Box className={styles.requestCard}>
            <Text className={styles.cardTitle}>Waiting for your approval</Text>
            <Group gap="xs" grow mt={10}>
              <Button
                color="teal"
                onClick={() => setStatus("Accepted")}
                loading={updating}
                disabled={!onStatusUpdate}
              >
                Accept
              </Button>
              <Button
                variant="default"
                onClick={() => setStatus("Declined")}
                disabled={!onStatusUpdate || updating}
              >
                Decline
              </Button>
            </Group>
          </Box>
        )}
        {area?.kind === "markArrived" && (
          <Box className={styles.arriveCard}>
            <Text className={styles.cardTitle}>{area.copy}</Text>
            <Button
              color="teal"
              h={40}
              mt={10}
              fullWidth
              leftSection={<IconUserCheck size={17} />}
              onClick={() => setStatus("Arrived")}
              loading={updating}
              disabled={!onStatusUpdate}
            >
              Mark arrived
            </Button>
          </Box>
        )}
        {area?.kind === "arrived" && (
          <Group
            justify="space-between"
            wrap="nowrap"
            className={styles.arrivedStrip}
            data-testid="arrived-strip"
          >
            <Group gap={6} wrap="nowrap">
              <IconCheck size={16} />
              <Text className={styles.arrivedText}>{area.label}</Text>
            </Group>
            <Button
              variant="subtle"
              size="compact-sm"
              className={styles.undo}
              onClick={() => setStatus("Confirmed")}
              disabled={!onStatusUpdate || updating}
            >
              Undo
            </Button>
          </Group>
        )}

        <NeedToKnow
          flags={withoutTagsFlag(
            badgeSettings
              ? withPinnedNotes(
                  flags,
                  notesApi.popupNotes.map((n) => n.text),
                  badgeSettings,
                )
              : flags,
          )}
        />

        {/* Summary rows → nested screens (label + count only) */}
        <Stack gap={8} mt={16}>
          {canViewClientForms && (
            <SummaryRow
              label="Forms"
              count={formsApi.totalCount}
              onOpen={() => onOpenNested("forms")}
            />
          )}
          <SummaryRow
            label="Scheduled messages"
            count={messagesCount}
            onOpen={() => onOpenNested("messages")}
          />
          <SummaryRow
            label="Notes"
            count={notesApi.notes.length}
            onOpen={() => onOpenNested("notes")}
          />
        </Stack>
      </Box>

      {/* Sticky footer */}
      <Stack gap={8} className={styles.footer}>
        <Button color="teal" h={44} fullWidth onClick={onCheckout}>
          View
        </Button>
        {showReschedule && (
          <Button
            variant="default"
            color="teal"
            h={44}
            fullWidth
            className={styles.rescheduleBtn}
            onClick={() => onReschedule?.(transaction)}
          >
            Edit / Reschedule
          </Button>
        )}
        <Group gap={8} wrap="nowrap">
          {showPrebook && (
            <Button
              variant="default"
              h={44}
              style={{ flex: 1 }}
              onClick={() => onRebook?.(transaction)}
            >
              Prebook
            </Button>
          )}
          <Menu position="top-end" width={250} radius={12} shadow="md">
            <Menu.Target>
              <ActionIcon
                variant="default"
                size={44}
                radius={8}
                aria-label="More actions"
                ml={showPrebook ? undefined : "auto"}
              >
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {!closed && (
                <Menu.Item
                  onClick={() => setStatus("No Show")}
                  disabled={!onStatusUpdate}
                >
                  Mark as no-show
                </Menu.Item>
              )}
              {!closed && <Menu.Divider />}
              <Menu.Item
                className={styles.destructive}
                onClick={() => setStatus("Cancelled")}
                disabled={!onStatusUpdate || status === "Cancelled"}
              >
                Cancel appointment
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Stack>
    </Box>
  );
}

const Fact = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Group gap={8} wrap="nowrap" align="flex-start" className={styles.fact}>
    <Box className={styles.factIcon}>{icon}</Box>
    <Text className={styles.factText}>{children}</Text>
  </Group>
);
